import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useBillingLifecycle } from "@/lib/billingLifecycle";
import { useTheme } from "@/lib/theme";
import api from "@/lib/api";
import "@/styles/workspaceBranding.css";

const Context = createContext({ displayName: "Fieldlink NZ", isWhiteLabel: false, assets: {} });
export const useWorkspaceBranding = () => useContext(Context);
const colour = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value || "") ? value : fallback;
export function brandingTokens(settings = {}) {
  const primary = colour(settings.primary_colour, "#FF5F15");
  const accent = colour(settings.secondary_colour, primary);
  const rgb = primary.slice(1).match(/../g).map(v => parseInt(v, 16));
  const luminance = rgb.map(v => v / 255).map(v => v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4);
  const light = luminance[0] * .2126 + luminance[1] * .7152 + luminance[2] * .0722;
  return { "--brand-primary": primary, "--brand-accent": accent,
    "--brand-on-primary": light > .179 ? "#000000" : "#FFFFFF",
    "--brand-primary-hover": "#" + rgb.map(v => Math.round(light > .179 ? v + (255 - v) * .08 : v * .9).toString(16).padStart(2, "0")).join(""),
    "--brand-hover-filter": "none" };
}

// Private images are fetched with session authentication, never tokens in URLs.
export function useBrandingAssets(settings, enabled = true) {
  const { user } = useAuth();
  const ids = [settings?.logo_light, settings?.logo_dark, settings?.app_icon].filter(Boolean).sort().join(",");
  const key = `${user?.id}:${user?.org_id}:${enabled}:${ids}`;
  const [snapshot, setSnapshot] = useState(null);
  useEffect(() => {
    let live = true;
    const urls = [];
    if (enabled && ids && user?.org_id) {
      Promise.all(ids.split(",").map(async id => {
        try {
          const { data } = await api.get(`/org/workspace-branding/assets/${id}`, { responseType: "blob" });
          if (!live) return [id, null];
          const url = URL.createObjectURL(data); urls.push(url); return [id, url];
        } catch { return [id, null]; }
      })).then(entries => { if (live) setSnapshot({ key, assets: Object.fromEntries(entries) }); });
    }
    return () => { live = false; urls.forEach(url => URL.revokeObjectURL(url)); };
  }, [key, ids, enabled, user?.org_id]);
  return snapshot?.key === key ? snapshot.assets : {};
}

export function WorkspaceBrandingProvider({ children }) {
  const { user } = useAuth();
  const { data, error, refresh } = useBillingLifecycle();
  const [clock, setClock] = useState(Date.now());
  const eligible = !!data?.white_label_enabled && !error && user?.role !== "super_admin";
  const ended = data?.cancel_at_period_end && Date.parse(data.current_period_end) <= clock;
  const settings = eligible && !ended ? data?.workspace_branding || {} : {};
  const active = Object.entries(settings).some(([key, value]) => value && !key.startsWith("updated_"));
  const assets = useBrandingAssets(settings, active);
  const value = useMemo(() => ({ settings, assets, displayName: settings.display_name || "Fieldlink NZ",
    isWhiteLabel: active, hideFieldlinkBranding: active && !!settings.hide_fieldlink_branding,
    tokens: brandingTokens(settings) }), [settings, assets, active]);

  useEffect(() => {
    const focus = () => { if (document.visibilityState !== "hidden") refresh(); };
    window.addEventListener("focus", focus);
    // Reuse the session's single lifecycle request; no route-level branding fetches.
    const timer = setInterval(() => { setClock(Date.now()); if (document.visibilityState !== "hidden") refresh(); }, 60000);
    const end = Date.parse(data?.current_period_end);
    const expiry = eligible && data?.cancel_at_period_end && end > Date.now() && end - Date.now() < 2147483647
      ? setTimeout(() => { setClock(Date.now()); refresh(); }, end - Date.now() + 10) : null;
    return () => { clearInterval(timer); clearTimeout(expiry); window.removeEventListener("focus", focus); };
  }, [refresh, eligible, data?.cancel_at_period_end, data?.current_period_end]);

  useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    Object.entries(value.tokens).forEach(([key, token]) => root.style.setProperty(key, token));
    root.dataset.workspaceBranding = "true";
    return () => { delete root.dataset.workspaceBranding; Object.keys(value.tokens).forEach(key => root.style.removeProperty(key)); };
  }, [active, value.tokens]);
  const icon = assets[settings.app_icon];
  useEffect(() => {
    if (!icon || !active) return;
    const links = [...document.querySelectorAll('link[rel="icon"]')];
    const originals = links.map(link => ({ link, href: link.getAttribute("href"), type: link.getAttribute("type"), sizes: link.getAttribute("sizes") }));
    links.forEach(link => { link.href = icon; link.type = "image/png"; link.removeAttribute("sizes"); });
    return () => originals.forEach(({ link, href, type, sizes }) => {
      for (const [key, val] of Object.entries({ href, type, sizes })) val == null ? link.removeAttribute(key) : link.setAttribute(key, val);
    });
  }, [icon, active]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function WorkspaceIdentity({ compact = false, dark, className = "" }) {
  const branding = useWorkspaceBranding();
  const { resolved } = useTheme();
  const isDark = dark ?? resolved === "dark";
  const { settings = {}, assets = {} } = branding;
  const src = assets[isDark ? settings.logo_dark : settings.logo_light] || assets[settings.logo_light] || assets[settings.logo_dark];
  const [failed, setFailed] = useState(null);
  const logoBackground = isDark && !assets[settings.logo_dark] && assets[settings.logo_light] ? "#ffffff"
    : !isDark && !assets[settings.logo_light] && assets[settings.logo_dark] ? "#101b2b" : undefined;
  return <div className={`workspace-identity ${className}`}>
    <img src={src && failed !== src ? src : "/brand/fieldlink-mark.png"} onError={() => setFailed(src)} alt={branding.displayName} style={{ background: logoBackground, borderRadius: 4 }}
      className={`object-contain shrink-0 ${compact ? "h-8 max-w-10" : "h-9 max-w-24"}`} />
    {!compact && <div className="min-w-0"><div className="font-display font-black leading-tight truncate">{branding.displayName}</div>
      {branding.isWhiteLabel && !branding.hideFieldlinkBranding && <div className="text-xs opacity-70 mt-1">Powered by Fieldlink</div>}
      {!branding.isWhiteLabel && <div className="text-xs opacity-70 mt-1">Built for what’s next</div>}
    </div>}
  </div>;
}
